import * as yup from 'yup';
import {setLocale, TestContext} from 'yup';
import {App} from 'vue';
import {find, isEmpty, isNumber, isString} from 'lodash-es';
import {IDrawZone} from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar/kl-draw-zone-sidebar';
import kinks from '@turf/kinks';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import WKT from 'ol/format/WKT';
import DateUtil from '@/app/shared/helpers/date-util';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import * as KlipApi from '@/api/klip-api.proxy';
import * as klYupLocales from './kl-yup-locales';
import dayjs from 'dayjs';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// WARNING: keep 'kl-yup.d.ts' in sync
// ref: https://www.npmjs.com/package/yup#extending-built-in-schema-with-new-methods
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const defineYupRules = () => {

    // yup.addMethod(yup.mixed, 'notRequired', function _notRequired() {
    //     return this
    //         .test('notRequired',
    //             ({value, label}) => 'sehdfgjhsbf',
    //             (value) => {
    //                 return !!value;
    //             })
    // });

    // Ik vind geen mogelijkheid om yup number validaties goed te laten werken voor het yup.number() schema, vandaar yup.string().kl_number()
    // problemen:
    // - combinatie met required/notRequired
    // - combinatie met standaard browser typecast & checks op 'input type=number'
    // - vullen + opnieuw leeg maken van een veld
    // - '-'/'-1-'/'--' als input geeft rare effecten
    yup.addMethod(yup.string, 'kl_number', function kl_number(options?: {min?: number, max?: number}) {

        function _getErrorMessage(value: string, label: string = '<placeholder>'): string | null {
            if (value === '' || value === undefined || value === null) {
                return null;
            }
            if (!isNumber(+value) || (+value).toString() !== value) {
                return `${label} is geen getal`;
            }
            const valueAsNumber = +value;
            if ((options?.min !== undefined) && (valueAsNumber < options?.min)) {
                return `${label} moet groter zijn dan of gelijk aan ${options?.min}`;
            }
            if ((options?.max !== undefined) && (valueAsNumber > options?.max)) {
                return `${label} moet kleiner zijn dan of gelijk aan ${options?.max}`;
            }
            return null
        }

        return this
            .test('kl_number',
                ({value, label}) => _getErrorMessage(value, label),
                (value) => {
                    const errorMessage = _getErrorMessage(value);
                    return !errorMessage;
                })
    });

    yup.addMethod(yup.object, 'kl_zoneSmallerThan', function kl_zoneSmallerThan(max: number, maxLabel: string) {
        return this
            .test('zone-smaller-than',
                () => `De oppervlakte van de zone moet kleiner zijn dan ${maxLabel} m².`,
                (zone: IDrawZone) => {
                    return zone.area < max;
                })
    });

    yup.addMethod(yup.object, 'kl_zoneShorterThan', function kl_zoneShorterThan(max: number, maxLabel: string) {
        return this
            .test('zone-shorter-than',
                () => `De omtrek van de zone moet kleiner zijn dan ${maxLabel} meter.`,
                (zone: IDrawZone) => {
                    return zone.length < max;
                })
    });

    yup.addMethod(yup.object, 'kl_notSelfIntersectingPolygon', function kl_notSelfIntersectingPolygon() {
        return this
            .test('zone-self-intersecting',
                () => `De zone is niet geldig: de zijden mogen elkaar niet snijden.`,
                (zone: IDrawZone) => {
                    if (!kinks(zone as any).features.length) {
                        return true;
                    }
                })
    });

    yup.addMethod(yup.object, 'kl_zonePartiallyInFlanders', function kl_zonePartiallyInFlanders() {
        return this
            .test('kl_zonePartiallyInFlanders',
                () => `De zone moet minstens een deel in Vlaanderen liggen.`,
                async (value: IDrawZone) => {
                    const input = KlipApi.IsGeometryIntersectingFlandersInput.fromJS({
                        geometry: JSON.stringify(value),
                    });
                    const validationResult = await useKlipApiProxy().validation_IsGeometryIntersectingFlanders(input);
                    if (validationResult.result) {
                        return true;
                    }

                    return false;
                })
    });

    yup.addMethod(yup.object, 'kl_zoneInBufferedFlanders', function kl_zoneInBufferedFlanders() {
        return this
            .test('kl_zoneInBufferedFlanders',
                () => `De zone mag niet meer dan 50m buiten Vlaanderen liggen.`,
                async (value: IDrawZone) => {
                    const input = KlipApi.IsGeometryWithinBufferedFlandersInput.fromJS({
                        geometry: JSON.stringify(value),
                    });
                    const validationResult = await useKlipApiProxy().validation_IsGeometryWithinBufferedFlanders(input);
                    if (validationResult.result) {
                        return true;
                    }

                    return false;
                })
    });

    yup.addMethod(yup.object, 'kl_isPolygon', function kl_isPolygon() {
        return this
            .test('kl_isPolygon',
                () => `De zone moet een polygoon zijn. Punten en lijnen zijn niet toegelaten.`,
                async (value: IDrawZone) => {
                    if (!!value.area) {
                        return true;
                    }

                    return false;
                })
    });

    yup.addMethod(yup.string, 'kl_phone', function kl_phone() {
        return this
            .test('phone',
                () => `Dit is geen geldig telefoonnummer.`,
                (value: string) => {
                    if (isEmpty(value)) {
                        return true;
                    }
                    const pattern = /(^|,)(^([\\+]?([0-9 \\(\\)\\/\\-\\.]){9,20}))$/;
                    const phoneString = value.toString();

                    return pattern.test(phoneString);
                })
    });

    yup.addMethod(yup.string, 'kl_phone_with_112', function kl_phone_with_112() {
        return this
            .test('phone_with_112',
                () => `Dit is geen geldig telefoonnummer.`,
                (value: string) => {
                    if (isEmpty(value)) {
                        return true;
                    }
                    const pattern = /(^|,)(^([\\+]?([0-9 \\(\\)\\/\\-\\.]){9,20})|^112)$/;
                    const phoneString = value.toString();

                    return pattern.test(phoneString);
                })
    });

    yup.addMethod(yup.array, 'kl_max_40_days_ahead', function kl_max40DaysAhead() {
        return this
            .test('max_40_days_ahead',
                () => `De startdatum mag maximaal 40 werkdagen in de toekomst liggen.`,
                (value: string[]) => {
                    if (!value) {
                        return true;
                    }

                    const date = DateUtil.parseToDayJs(value);
                    const between = DateUtil.businessDaysBetween(DateUtil.today(), date);
                    return between <= 40;
                })
    });

    const klipEmailRegex = /^[\w.% '!#$&*/=?^_`{|}~+-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    const isValidKlipEmail = (value: string) => {
        return klipEmailRegex.test(value) && value.length <= 320;
    };

    yup.addMethod(yup.string, 'kl_validKlipEmail', function kl_validKlipEmail() {
        return this
            .test('kl_validKlipEmail',
                () => `Dit is geen geldig email adres.`,
                (value: string) => {
                    if (!value) {
                        return true;
                    }
                    return isValidKlipEmail(value);
                })
    });

    yup.addMethod(yup.string, 'kl_validKlipEmails', function validKlipEmails() {
        return this
            .test('validKlipEmails',
                () => `Bevat een ongeldig email adres.`,
                (value: string) => {
                    if (!value) {
                        return true;
                    }

                    const emailAddresses = value.split(";");
                    if (emailAddresses.length === 0 && value.trim().length > 0) {
                        return false;
                    }
                    return emailAddresses.every(isValidKlipEmail);
                })
    });

    yup.addMethod(yup.array, 'kl_valid_date', function kl_valid_date() {
        return this
            .test('kl_valid_date',
                () => `Dit is geen geldige datum.`,
                (value: string[]) => {
                    if (isEmpty(value) || isEmpty(value[0])) {
                        return true;
                    }
                    return DateUtil.isValidDateFormat(value[0]);
                })
    });

    yup.addMethod(yup.array, 'kl_required_date', function kl_required_date() {
        return this
            .test('kl_required_date',
                ({value, label}) => {
                    if (isEmpty(value) || isEmpty(value[0])) {
                        return `${label} is verplicht`;
                    }
                    return `Dit is geen geldige datum.`;
                },
                (value: string[]) => {
                    if (isEmpty(value) || isEmpty(value[0])) {
                        return false;
                    }
                    return DateUtil.isValidDateFormat(value[0]);
                })
    });

    yup.addMethod(yup.array, 'kl_date_later_then', function kl_date_later_then(options?: {minDate?: dayjs.Dayjs, minDateField?: string}) {

        function _getMinDate(context: TestContext): dayjs.Dayjs {
            const minDate = DateUtil.parseToDayJs(options?.minDate);
            const referencedMinDate = context?.parent ? DateUtil.parseToDayJs(context?.parent[options?.minDateField]) : null;

            if (!referencedMinDate) {
                return minDate;
            }
            if (!options?.minDate) {
                return referencedMinDate;
            }

            // return biggest min date
            return referencedMinDate > minDate ? referencedMinDate : minDate;
        }

        return this
            .test('kl_date_later_then',
                ({value, label}) => {
                    return `${label} moet later zijn`;
                },
                (value: string[], context: TestContext) => {
                    if (isEmpty(value)) {
                        return true;
                    }
                    const dateValue = DateUtil.parseToDayJs(value);
                    if (!dateValue) {
                        return true;
                    }

                    const minDate: dayjs.Dayjs = _getMinDate(context);
                    if (!minDate) {
                        return true;
                    }
                    return minDate <= dateValue;
             })
    });

    yup.addMethod(yup.object, 'kl_zoneGeometrySmallerThanXBytes', function kl_zoneGeometrySmallerThanXBytes(max: number) {
        return this
            .test('kl_zoneGeometrySmallerThanXBytes',
                () => `De zone bevat te veel coördinaten.`,
                (zone: IDrawZone) => {
                    let geometry = null;

                    if (zone.type === 'Polygon') {
                        geometry = new Polygon(zone.coordinates as number[][][]);
                    }
                    if (zone.type === 'MultiPolygon') {
                        // TODO: verify
                        geometry = new MultiPolygon(zone.coordinates as number[][][][]);
                    }

                    if (!geometry) {
                        console.warn('zone_geometry_smaller_than_x_bytes without valid geometry');
                        return false;
                    }

                    const wkt: string = new WKT().writeGeometry(geometry);
                    // const size = new Blob([wkt]).size; // = would be correct size

                    // current server-side logic uses c# unicode-16 string
                    // > #bytes = wkt.length * 2
                    const size = wkt.length * 2;


                    if (size < Number(max)) {
                        return true;
                    }

                    return false;
                })
    });

    yup.addMethod(yup.string, 'kl_vatNumberValid', function kl_vatNumberValid() {
        return this
            .test('is-valid-vat',
                () => 'Het opgegeven btw-nummer is ongeldig. Contacteer accounting@athumi.eu indien u toch facturen op dit btw-nummer wenst.',
                (input: string) => {

                    // add 'required' rule when this is not valid
                    if (isEmpty(input)) {
                        return true;
                    }
                    // if(isCitizen && (!input || input.length === 0)){
                    //     return true;
                    // }

                    const validCountry = [
                        'AT',
                        'BE',
                        'BG',
                        'CY',
                        'CZ',
                        'DE',
                        'DK',
                        'EE',
                        'ES',
                        'FI',
                        'FR',
                        'GB',
                        'EL',
                        'HR',
                        'HU',
                        'IE',
                        'IT',
                        'LT',
                        'LU',
                        'LV',
                        'MT',
                        'NL',
                        'PL',
                        'PT',
                        'RO',
                        'SE',
                        'SI',
                        'SK'
                    ];

                    if (!input || input.length < 4) {
                        return false;
                    }


                    if (!(input.match(/^[a-zA-Z]{2}.*/))) {
                        return false;
                    }

                    const country = input.substring(0, 2);
                    if (!validCountry.includes(country.toUpperCase())) {
                        return false;
                    }

                    if (country.toUpperCase() !== 'BE') {
                        return true;
                    }

                    const inputNumber = parseInt(input.substring(2, input.length));
                    const inputChecksum = parseInt(input.substring(input.length - 2, input.length));

                    if (inputChecksum === Number.NaN || inputNumber === Number.NaN) {
                        return false;
                    }

                    const result = 97 - (((inputNumber - (inputNumber % 100)) / 100) - (((inputNumber - (inputNumber % 100)) / 100 / 97 - (inputNumber - (inputNumber % 100)) / 100 / 97 % 1) * 97));
                    const result97 = (result === 0) ? 97 : result;
                    if (inputChecksum === result97) {
                        return true;
                    }

                    return false;
                });
    });

    yup.addMethod(yup.string, 'kl_organisationDisplayNameIsUnique', function kl_organisationDisplayNameIsUnique(initialNameField: string) {
        return this
            .test('kl_organisationDisplayNameIsUnique',
                () => `Er bestaat al een organisatie met deze naam.`,
                async (name: string, context: TestContext) => {
                    const initialName = context.parent[initialNameField];
                    console.assert(initialName !== undefined, `Field '${initialNameField}' does not exist in parent schema.`);
                    if (initialName === name) {
                        return true;
                    }
                    const exist = await useKlipApiProxy().unaSettings_DisplayNameExists(name);
                    return !exist.result;
                })
    });

    yup.addMethod(yup.string, 'kl_uniqueNamespace', function kl_uniqueNamespace(zoneIdField: string) {
        return this
            .test('kl_uniqueNamespace',
                () => `Er bestaat al een zone met deze namespace.`,
                async (namespace: string, context: TestContext) => {
                    const zoneId = context.parent[zoneIdField];
                    console.assert(zoneId !== undefined, `Field '${zoneIdField}' does not exist in parent schema.`);

                    const exist = await useKlipApiProxy().unaSettings_NamespaceExists(namespace, zoneId);
                    return !exist.result;
                })
    });

    yup.addMethod(yup.string, 'kl_uniqueZoneName', function kl_uniqueZoneName(zoneIdField: string, initialNameField: string) {
        return this
            .test('kl_uniqueZoneName',
                () => `Er bestaat al een zone met deze naam.`,
                async (name: string, context: TestContext) => {
                    const zoneId = context.parent[zoneIdField];
                    const initialName = context.parent[initialNameField];
                    console.assert(zoneId !== undefined, `Field '${zoneIdField}' does not exist in parent schema.`);
                    console.assert(initialName !== undefined, `Field '${initialNameField}' does not exist in parent schema.`);

                    if (initialName === name) {
                        return true;
                    }
                    const exist = await useKlipApiProxy().unaSettings_ZoneNameExist(name, zoneId);
                    return !exist.result;
                })
    });

    yup.addMethod(yup.array, 'kl_tags', function kl_tags() {
        return this
            .test('kl_tags',
                () => `Een tag mag geen komma bevatten en moet tussen 3 en 100 karakters lang zijn.`,
                (values: string[]) => {
                    const ok = !(find(values, (tag) => tag.includes(',')) || find(values, (tag) => tag.length < 3 || tag.length > 100));
                    return ok;
            })
    });
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Locale has to be set BEFORE any schema is defined.
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const setKlipLocales = () => {
    setLocale(klYupLocales);
}

export default {
    install(app: App): void {
        setKlipLocales();
        defineYupRules();
    },
};
